/**
 * Recording Storage Service - Dual Storage Implementation
 * Supports both local IndexedDB storage and Supabase cloud storage
 * Follows the same pattern as audioStorageService.js
 */

import * as recordingIndexedDB from "../utils/recordingIndexedDB";

// API base URL helper
const getApiBase = () => {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname.includes("netlify.app") ||
      window.location.hostname.includes(".netlify.com") ||
      window.location.hostname.includes("lyrical-toolkit.com"))
  ) {
    return "";
  }
  return "https://lyrical-toolkit.netlify.app";
};

/**
 * Check recording limit before upload (cloud mode)
 */
export const checkRecordingLimit = async (userId, songId) => {
  const API_BASE = getApiBase();
  console.log("🔍 Checking recording limit for user:", userId, "song:", songId);

  try {
    const response = await fetch(
      `${API_BASE}/.netlify/functions/check-recording-limit`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, songId }),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to check recording limit");
    }

    const result = await response.json();
    console.log("✅ Recording limit check:", result);
    return result;
  } catch (error) {
    console.error("❌ Error checking recording limit:", error);
    throw error;
  }
};

/**
 * Check local storage limits
 */
export const checkLocalRecordingLimit = async (songId, limits) => {
  const perSongLimit = limits?.recordingLimitPerSongLocal ?? 3;
  const totalLimit = limits?.recordingLimitTotalLocal ?? 10;

  const currentPerSong = await recordingIndexedDB.countRecordings(songId);
  const currentTotal = await recordingIndexedDB.countAllRecordings();

  const perSongAllowed =
    perSongLimit === 999999 || currentPerSong < perSongLimit;
  const totalAllowed = totalLimit === 999999 || currentTotal < totalLimit;

  return {
    allowed: perSongAllowed && totalAllowed,
    currentPerSong,
    limitPerSong: perSongLimit,
    currentTotal,
    limitTotal: totalLimit,
    message: !perSongAllowed
      ? `Take limit reached for this song (${currentPerSong}/${perSongLimit}).`
      : !totalAllowed
        ? `Total recording limit reached (${currentTotal}/${totalLimit}).`
        : null,
  };
};

/**
 * Upload recording to Supabase Storage via signed URL
 */
const uploadToSupabase = async (blob, songId, userId, takeNumber) => {
  const API_BASE = getApiBase();
  // Derive extension from actual MIME type — Android WebView may use mp4
  const ext = blob.type && blob.type.includes("mp4") ? "mp4" : "webm";
  const filename = `take-${takeNumber}-${Date.now()}.${ext}`;

  console.log("☁️ Uploading recording to Supabase:", filename);

  // Step 1: Get signed upload URL
  const urlResponse = await fetch(
    `${API_BASE}/.netlify/functions/get-recording-upload-url`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        songId,
        userId,
        takeNumber,
        filename,
        contentType: blob.type || "audio/webm",
      }),
    },
  );

  if (!urlResponse.ok) {
    const error = await urlResponse.json();
    throw new Error(error.message || "Failed to get recording upload URL");
  }

  const { uploadUrl, publicUrl, filePath } = await urlResponse.json();

  // Step 2: Upload directly to Supabase
  // Use the original un-patched fetch when running inside Capacitor.
  // CapacitorHttp patches window.fetch to route through the native bridge,
  // which corrupts binary Blob bodies during serialization.  The original
  // browser fetch is saved as window.CapacitorWebFetch.  Supabase Storage
  // has proper CORS headers so the native CORS bypass isn't needed here.
  const binaryFetch = window.CapacitorWebFetch || window.fetch;
  const uploadResponse = await binaryFetch.call(window, uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": blob.type || "audio/webm",
      "x-upsert": "false",
    },
    body: blob,
  });

  if (!uploadResponse.ok) {
    const error = await uploadResponse.text();
    throw new Error(`Recording upload failed: ${error}`);
  }

  console.log("✅ Recording uploaded to Supabase");

  return { publicUrl, filePath, filename };
};

/**
 * Save recording metadata to Supabase
 */
const saveMetadataToSupabase = async (
  songId,
  userId,
  recordingUrl,
  metadata,
) => {
  const API_BASE = getApiBase();

  const response = await fetch(
    `${API_BASE}/.netlify/functions/song-recordings`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        songId,
        userId,
        label: metadata.label,
        recordingUrl,
        filename: metadata.filename,
        fileSize: metadata.size,
        duration: metadata.duration,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to save recording metadata");
  }

  const result = await response.json();
  return result.recording;
};

/**
 * Upload a recording - chooses storage based on storageType
 */
export const uploadRecording = async (
  blob,
  songId,
  userId = "anonymous",
  storageType = "local",
  metadata = {},
) => {
  console.log("🎤 Uploading recording to", storageType, "storage");

  try {
    if (storageType === "database") {
      // Cloud mode: upload to Supabase Storage + create metadata record
      const takeNumber = metadata.takeNumber || Date.now();
      const { publicUrl, filePath, filename } = await uploadToSupabase(
        blob,
        songId,
        userId,
        takeNumber,
      );

      const recording = await saveMetadataToSupabase(
        songId,
        userId,
        publicUrl,
        {
          label: metadata.label || `Take ${takeNumber}`,
          filename,
          size: blob.size,
          duration: metadata.duration || 0,
          filePath,
        },
      );

      return normalizeRecording(recording);
    } else {
      // Local mode: save to IndexedDB
      const takeNumber =
        metadata.takeNumber ||
        (await recordingIndexedDB.getNextTakeNumber(songId));
      const saved = await recordingIndexedDB.saveRecording(
        songId,
        takeNumber,
        blob,
        {
          label: metadata.label || `Take ${takeNumber}`,
          filename: `take-${takeNumber}.${blob.type && blob.type.includes("mp4") ? "mp4" : "webm"}`,
          duration: metadata.duration || 0,
        },
      );

      return {
        id: saved.id,
        songId: saved.songId,
        takeNumber: saved.takeNumber,
        label: saved.label,
        filename: saved.filename,
        size: saved.size,
        duration: saved.duration,
        type: saved.type,
        createdAt: saved.createdAt,
        recordingUrl: `indexeddb-recording://${saved.id}`,
      };
    }
  } catch (error) {
    console.error("❌ Error uploading recording:", error);
    throw error;
  }
};

/**
 * List recordings for a song
 */
export const listRecordings = async (songId, userId, storageType = "local") => {
  try {
    if (storageType === "database") {
      const API_BASE = getApiBase();
      const params = new URLSearchParams({ songId, userId });
      const response = await fetch(
        `${API_BASE}/.netlify/functions/song-recordings?${params}`,
        { method: "GET", headers: { "Content-Type": "application/json" } },
      );

      if (!response.ok) throw new Error("Failed to list recordings");

      const { recordings } = await response.json();
      return (recordings || []).map(normalizeRecording);
    } else {
      return await recordingIndexedDB.listRecordings(songId);
    }
  } catch (error) {
    console.error("❌ Error listing recordings:", error);
    return [];
  }
};

/**
 * Get a recording's playback URL
 */
export const getRecordingUrl = async (recording, storageType = "local") => {
  try {
    if (storageType === "database") {
      // Cloud recordings already have a public URL
      return recording.recordingUrl || recording.recording_url;
    } else {
      // Local: get from IndexedDB and create blob URL
      const data = await recordingIndexedDB.getRecording(
        recording.songId,
        recording.takeNumber,
      );
      if (!data) throw new Error("Recording not found in IndexedDB");
      return recordingIndexedDB.createRecordingBlobURL(data);
    }
  } catch (error) {
    console.error("❌ Error getting recording URL:", error);
    throw error;
  }
};

/**
 * Delete a recording
 */
export const deleteRecording = async (
  recording,
  userId,
  storageType = "local",
) => {
  try {
    if (storageType === "database") {
      const API_BASE = getApiBase();
      const response = await fetch(
        `${API_BASE}/.netlify/functions/song-recordings`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: recording.id,
            userId,
            filePath: recording.filePath || null,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to delete recording");
      return true;
    } else {
      await recordingIndexedDB.deleteRecording(
        recording.songId,
        recording.takeNumber,
      );
      return true;
    }
  } catch (error) {
    console.error("❌ Error deleting recording:", error);
    throw error;
  }
};

/**
 * Rename a recording
 */
export const renameRecording = async (
  recording,
  newLabel,
  userId,
  storageType = "local",
) => {
  try {
    if (storageType === "database") {
      const API_BASE = getApiBase();
      const response = await fetch(
        `${API_BASE}/.netlify/functions/song-recordings`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: recording.id, userId, label: newLabel }),
        },
      );

      if (!response.ok) throw new Error("Failed to rename recording");
      const { recording: updated } = await response.json();
      return normalizeRecording(updated);
    } else {
      return await recordingIndexedDB.updateRecordingLabel(
        recording.songId,
        recording.takeNumber,
        newLabel,
      );
    }
  } catch (error) {
    console.error("❌ Error renaming recording:", error);
    throw error;
  }
};

/**
 * Normalize cloud recording response (snake_case → camelCase)
 */
const normalizeRecording = (rec) => ({
  id: rec.id,
  songId: rec.song_id || rec.songId,
  takeNumber: rec.take_number || rec.takeNumber,
  label: rec.label,
  recordingUrl: rec.recording_url || rec.recordingUrl,
  filename: rec.filename,
  size: rec.file_size || rec.size,
  duration: rec.duration,
  shiftOffsetSec: rec.shift_offset_sec ?? rec.shiftOffsetSec ?? 0,
  createdAt: rec.created_at || rec.createdAt,
});

/**
 * Format recording duration for display
 */
export const formatRecordingDuration = (seconds) => {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Update shift offset for a recording
 */
export const updateShiftOffset = async (
  recording,
  shiftOffsetSec,
  userId,
  storageType = "local",
) => {
  try {
    if (storageType === "database") {
      const API_BASE = getApiBase();
      const response = await fetch(
        `${API_BASE}/.netlify/functions/song-recordings`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            songId: recording.songId,
            takeNumber: recording.takeNumber,
            userId,
            shiftOffsetSec,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to update shift offset");
      const { recording: updated } = await response.json();
      return normalizeRecording(updated);
    } else {
      return await recordingIndexedDB.updateShiftOffset(
        recording.songId,
        recording.takeNumber,
        shiftOffsetSec,
      );
    }
  } catch (error) {
    console.error("❌ Error updating shift offset:", error);
    throw error;
  }
};

/**
 * Replace the audio blob for an existing recording (punch-in splice)
 * Preserves all metadata, only updates the audio data
 */
export const replaceRecordingAudio = async (
  recording,
  newBlob,
  duration,
  userId,
  storageType = "local",
) => {
  try {
    if (storageType === "database") {
      // Cloud: re-upload to Supabase and update metadata
      const { publicUrl } = await uploadToSupabase(
        newBlob,
        recording.songId,
        userId,
        recording.takeNumber,
      );
      // Update the recording URL in the database
      const API_BASE = getApiBase();
      const response = await fetch(
        `${API_BASE}/.netlify/functions/song-recordings`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: recording.id,
            userId,
            label: recording.label,
            recordingUrl: publicUrl,
            fileSize: newBlob.size,
            duration,
          }),
        },
      );
      if (!response.ok) throw new Error("Failed to update recording in cloud");
      const { recording: updated } = await response.json();
      return normalizeRecording(updated);
    } else {
      // Local: replace blob in IndexedDB
      await recordingIndexedDB.replaceRecordingBlob(
        recording.songId,
        recording.takeNumber,
        newBlob,
        duration,
      );
      return { ...recording, size: newBlob.size, duration };
    }
  } catch (error) {
    console.error("❌ Error replacing recording audio:", error);
    throw error;
  }
};

const recordingStorageService = {
  checkRecordingLimit,
  checkLocalRecordingLimit,
  uploadRecording,
  listRecordings,
  getRecordingUrl,
  deleteRecording,
  renameRecording,
  updateShiftOffset,
  replaceRecordingAudio,
  formatRecordingDuration,
};

export default recordingStorageService;
